/**
 * <s-zip-checker> — Checks a ZIP code against delivery zones and announces the result.
 *
 * Required child markup:
 *   form[data-zip-form]         — Submitting it runs the check (native submit is prevented).
 *   input[name="zip"]           — The ZIP field. The first five digits are used.
 *   [data-zip-result]           — Polite live region (role="status"); receives the message and data-state.
 *   [data-delivery-zone]        — One per zone with data-zips (comma-separated), data-days and data-zone-name.
 *
 * Host data attributes (messages; [ZIP], [DAYS] and [ZONE] are replaced):
 *   data-success — Shown when the ZIP is in a zone.
 *   data-fail    — Shown when no zone covers the ZIP.
 *   data-invalid — Shown when the entry is not a 5-digit ZIP.
 *
 * Result states on [data-zip-result]: data-state="success" | "fail" | "invalid".
 */
class SZipChecker extends HTMLElement {
  #onSubmit = (event) => {
    event.preventDefault();
    this.#check();
  };

  get #form() {
    return this.querySelector('[data-zip-form]');
  }

  connectedCallback() {
    this.#form?.addEventListener('submit', this.#onSubmit);
  }

  disconnectedCallback() {
    this.#form?.removeEventListener('submit', this.#onSubmit);
  }

  #check() {
    const input = this.querySelector('input[name="zip"]');
    const result = this.querySelector('[data-zip-result]');
    if (!input || !result) return;

    const digits = input.value.replace(/\D/g, '').slice(0, 5);
    if (digits.length !== 5) {
      this.#announce(result, 'invalid', this.dataset.invalid || 'Please enter a 5-digit ZIP code', {});
      input.setAttribute('aria-invalid', 'true');
      input.focus();
      return;
    }
    input.removeAttribute('aria-invalid');

    const zone = [...this.querySelectorAll('[data-delivery-zone]')].find((candidate) =>
      (candidate.dataset.zips || '')
        .split(',')
        .map((zip) => zip.trim())
        .includes(digits),
    );

    if (zone) {
      this.#announce(result, 'success', this.dataset.success || 'Yes! We deliver to [ZIP] on [DAYS]', {
        ZIP: digits,
        DAYS: zone.dataset.days || '',
        ZONE: zone.dataset.zoneName || '',
      });
    } else {
      this.#announce(
        result,
        'fail',
        this.dataset.fail || "We don't deliver to [ZIP] yet, but we ship to the contiguous 48 states (frozen items excluded), or pick up free in Cedar Rapids.",
        { ZIP: digits },
        { text: this.dataset.failLinkText, url: this.dataset.failLinkUrl },
      );
    }
  }

  #announce(result, state, template, values, link = {}) {
    const message = template.replace(/\[(ZIP|DAYS|ZONE)\]/g, (match, key) => values[key] ?? match);
    result.dataset.state = state;
    const failLink = this.querySelector('[data-zip-fail-link]');
    if (failLink) failLink.hidden = state !== 'fail';
    // Clear first so screen readers re-announce an identical repeated message.
    result.textContent = '';
    window.requestAnimationFrame(() => {
      const index = link.text && link.url ? message.indexOf(link.text) : -1;
      if (index === -1) {
        result.textContent = message;
        return;
      }
      // Turn the matching phrase into a link, built from text nodes so message text is never parsed as HTML.
      const anchor = document.createElement('a');
      anchor.href = link.url;
      anchor.textContent = link.text;
      anchor.className = 'text-interactive underline hover:no-underline';
      result.append(message.slice(0, index), anchor, message.slice(index + link.text.length));
    });
  }
}

if (!customElements.get('s-zip-checker')) customElements.define('s-zip-checker', SZipChecker);
